import {
  Col,
  Row,
  Button,
  Form,
  Input,
  Card,
  Space,
  Typography,
  Modal,
  Select,
  App
} from "antd";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import APICall, { fetchTenants } from "../services/apiservices";
import { serverURL } from "../services/apiservices";
import axios from "axios";
import { MailOutlined, LockOutlined, ArrowRightOutlined, SafetyOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useAuth } from "../auth/AuthProvider";
import { getLogo } from "../utils/organizationConfig";

const Login = () => {
  const navigate = useNavigate();
  const { setUserData } = useAuth();
  const { message } = App.useApp();
  const modalShownRef = useRef(false);
  const [currentCard, setCurrentCard] = useState("login"); // Track which card is displayed
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [email, setEmail] = useState("");
  const [timer, setTimer] = useState(60); // Timer for resend button
  const [timerActive, setTimerActive] = useState(false);
  const [transitionStyles, setTransitionStyles] = useState({ opacity: 1, transform: "translateY(0)" });
  const [otp, setOtp] = useState(""); // Store OTP
  const [resetPasswordToken, setResetPasswordToken] = useState(""); // Store token
  const [password, setPassword] = useState(""); // Store new password
  const [confirmPassword, setConfirmPassword] = useState(""); // Store confirm password
  const [loading, setLoading] = useState(false);
  const [tenants, setTenants] = useState([]); // Store list of tenants
  const [tenantsLoading, setTenantsLoading] = useState(false);

  useEffect(() => {
    if (!modalShownRef.current) {
      // Check if the user is using Chrome
      const isChrome =
        /Chrome/.test(navigator.userAgent) &&
        /Google Inc/.test(navigator.vendor) &&
        !/Edg/.test(navigator.userAgent); // Exclude Edge which is Chromium-based

      if (!isChrome) {
        Modal.warning({
          title: "Browser Compatibility",
          content: "Please use Chrome latest version to ensure functionality of all features.",
          centered: true,
        });
        modalShownRef.current = true; // Set the flag to true to prevent further popups
      }
    }
    if (localStorage.getItem("accessToken")) {
      navigate("/dashboard");
    }
  }, [navigate]);

  // Fetch tenants on component mount
  useEffect(() => {
    const loadTenants = async () => {
      try {
        setTenantsLoading(true);
        const tenantList = await fetchTenants();
        setTenants(tenantList);
      } catch (error) {
        console.error('Failed to load tenants:', error);
        message.error('Failed to load company list. Please refresh the page.');
      } finally {
        setTenantsLoading(false);
      }
    };
    loadTenants();
  }, [message]);

  useEffect(() => {
    let interval;
    if (timerActive && timer > 0) {
      interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    } else if (timer === 0) {
      setTimerActive(false);
    }
    return () => clearInterval(interval);
  }, [timerActive, timer]);

  const animateCardChange = (newCard) => {
    // Animate out
    setTransitionStyles({ opacity: 0, transform: "translateY(-10px)" });
    setTimeout(() => {
      setCurrentCard(newCard); // Change the card after the animation out
      setTransitionStyles({ opacity: 1, transform: "translateY(0)" }); // Animate in
    }, 300);
  };

  const onFinish = async (formData) => {
    console.log('[Login] onFinish called with formData:', formData);
    setLoading(true);
    try {
      // Ensure tenantId is included in the request
      if (!formData.tenantId) {
        console.log('[Login] No tenantId provided');
        message.error("Please select a company");
        setLoading(false);
        return;
      }
      
      console.log('[Login] Making login request to:', `${serverURL}/auth/login`);
      // Make login request with tenantId in body and header
      // Use axios directly to set X-Tenant-ID header explicitly
      const response = await axios.post(
        `${serverURL}/auth/login`,
        formData,
        {
          headers: {
            'X-Tenant-ID': formData.tenantId,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('[Login] Login response received:', {
        hasAccessToken: !!response.data.accessToken,
        hasUser: !!response.data.user,
        userKeys: response.data.user ? Object.keys(response.data.user) : [],
        userAccess: response.data.user?.access,
        userEmployee: response.data.user?.employee,
        fullUserData: response.data.user
      });
      
      if (response.data.accessToken) {
        console.log('[Login] Setting localStorage items');
        localStorage.setItem("accessToken", response.data.accessToken);
        
        // Store user with tenant information
        const userDataWithTenant = {
          ...response.data.user,
          tenantId: response.data.tenant?.tenantId || response.data.tenant?.slug || formData.tenantId
        };
        localStorage.setItem("userData", JSON.stringify(userDataWithTenant));
        localStorage.setItem("tenant", JSON.stringify(response.data.tenant));
        
        console.log('[Login] localStorage after set:', {
          accessToken: localStorage.getItem("accessToken") ? 'SET' : 'NOT SET',
          userData: localStorage.getItem("userData") ? 'SET' : 'NOT SET',
          tenant: localStorage.getItem("tenant") ? 'SET' : 'NOT SET',
          userDataContent: localStorage.getItem("userData"),
          tenantContent: localStorage.getItem("tenant")
        });
        
        // Update the auth context with user data (including tenantId)
        console.log('[Login] Calling setUserData with:', userDataWithTenant);
        setUserData(userDataWithTenant);
        
        console.log('[Login] Calling navigate("/dashboard")');
        message.success("Login successful!");
        navigate("/dashboard");
        console.log('[Login] Navigate called, current location should change');
      } else {
        console.error('[Login] No accessToken in response');
      }
    } catch (error) {
      console.error('[Login] Error during login:', error);
      // Handle error
      if (error.response) {
        const { status, data } = error.response;
        if (status === 401) {
          message.error(data?.message || "Invalid email or password");
        } else if (status >= 500) {
          message.error("Server error. Please try again later.");
        } else {
          message.error(data?.message || "Login failed. Please try again.");
        }
      } else {
        message.error("Network error. Please check your connection.");
      }
    } finally {
      setLoading(false);
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };

  const handleForgotPassword = () => animateCardChange("forgotPassword");
  const handleBackToLogin = () => animateCardChange("login");

  const handleGetCode = async () => {
    if (!email) {
      message.error("Please enter your email address.");
      return;
    }

    setLoading(true);
    try {
      const response = await APICall("/auth/forgotPassword/sendOTP", { email }, 2);
      setIsCodeSent(true);
      setTimer(60);
      setTimerActive(true);
      message.success(response.data.message || "OTP sent!!");
    } catch (error) {
      message.error(error.response.data?.message || "Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!otp) {
      message.error("Please enter the OTP.");
      return;
    }

    setLoading(true);
    try {
      const response = await APICall("/auth/forgotPassword/verifyOTP", { email, otp }, 2);
      setResetPasswordToken(response.data.token); // Save token
      message.success("OTP verified!!");
      animateCardChange("resetPassword");
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to verify OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Updated to be used as the form onFinish handler in the Reset Password form.
  const handleUpdatePassword = async () => {
    if (!password) {
      message.error("Please enter a new password.");
      return;
    }

    if (password !== confirmPassword) {
      message.error("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      await APICall("/auth/forgotPassword/resetPassword", {
        email,
        resetPasswordToken,
        password,
      }, 2);
      message.success("Password updated successfully.");
      animateCardChange("login");
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to reset password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Common styles for cards
  const cardStyle = {
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
    borderRadius: '12px',
    border: 'none',
    overflow: 'hidden',
    width: '100%'
  };

  // Button styles
  const buttonStyle = {
    height: '40px',
    borderRadius: '6px',
    fontWeight: '500'
  };

  return (
    <div style={{
      backgroundImage: `linear-gradient(135deg, rgba(255, 165, 0, 0.6), rgba(255, 99, 71, 0.5), rgba(70, 130, 180, 0.4)), url(${process.env.PUBLIC_URL + '/login.png'})`,
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      height: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <Row justify="center" align="middle" style={{ width: '100%' }}>
        <Col xs={22} sm={18} md={12} lg={8} xl={7}>
          <Card
            style={{
              ...cardStyle,
              marginBottom: '2rem',
              textAlign: 'center'
            }}
            bodyStyle={{ padding: '20px 24px' }}
          >
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <img src={getLogo()} alt="Inferno ERP Logo" style={{ height: '70px', marginBottom: '10px' }} />
              <Typography.Title level={3} style={{ margin: '0', color: '#1a3b63' }}>
                Inferno ERP
              </Typography.Title>
              <Typography.Paragraph style={{ margin: '0.5rem 0 0', color: '#1a3b63', fontWeight: '500' }}>
                The Most Advanced Fire Safety Management Software
              </Typography.Paragraph>
            </Space>
          </Card>

          <div style={{
            transition: "all 400ms cubic-bezier(0.23, 1, 0.32, 1)",
            ...transitionStyles
          }}>
            {/* Login Card */}
            {currentCard === "login" && (
              <Card
                title={<div style={{ textAlign: 'center', fontSize: '20px', color: '#1a3b63' }}>Login</div>}
                style={cardStyle}
                styles={{ body: { padding: '20px 24px' } }}
              >
                <Form
                  name="login"
                  onFinish={onFinish}
                  onFinishFailed={onFinishFailed}
                  autoComplete="off"
                  layout="vertical"
                  size="large"
                >
                  <Form.Item
                    name="tenantId"
                    rules={[
                      { required: true, message: "Please select a company!" }
                    ]}
                  >
                    <Select
                      placeholder="Select Company"
                      loading={tenantsLoading}
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      style={{ borderRadius: '6px' }}
                      options={tenants.map(tenant => ({
                        value: tenant.tenantId || tenant.slug,
                        label: tenant.name || tenant.slug
                      }))}
                    />
                  </Form.Item>

                  <Form.Item
                    name="email"
                    rules={[
                      { required: true, message: "Please input your email!" },
                      { type: 'email', message: "Please enter a valid email!" }
                    ]}
                  >
                    <Input
                      prefix={<MailOutlined style={{ color: '#bfbfbf' }} />}
                      placeholder="Email Address"
                      style={{ borderRadius: '6px' }}
                    />
                  </Form.Item>

                  <Form.Item
                    name="password"
                    rules={[{ required: true, message: "Please input your password!" }]}
                  >
                    <Input.Password
                      prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
                      placeholder="Password"
                      style={{ borderRadius: '6px' }}
                    />
                  </Form.Item>

                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      block
                      style={buttonStyle}
                      loading={loading}
                      icon={<ArrowRightOutlined />}
                    >
                      Login
                    </Button>
                  </Form.Item>

                  <div style={{ textAlign: 'center' }}>
                    <a
                      onClick={(e) => {
                        e.preventDefault();
                        handleForgotPassword();
                      }}
                      style={{ color: '#1a3b63' }}
                    >
                      Forgot Password?
                    </a>
                  </div>
                </Form>
              </Card>
            )}

            {/* Forgot Password Card */}
            {currentCard === "forgotPassword" && (
              <Card
                title={<div style={{ textAlign: 'center', fontSize: '20px', color: '#1a3b63' }}>Reset Password</div>}
                style={cardStyle}
                styles={{ body: { padding: '20px 24px' } }}
              >
                <div style={{ position: 'absolute', top: '16px', left: '16px' }}>
                  <a
                    onClick={handleBackToLogin}
                    style={{ color: '#1a3b63', fontSize: '14px', display: 'flex', alignItems: 'center' }}
                  >
                    <ArrowLeftOutlined style={{ marginRight: '5px' }} /> Login
                  </a>
                </div>
                <Form layout="vertical" size="large">
                  <Form.Item
                    label={<span style={{ color: '#1a3b63' }}>Email Address</span>}
                    required
                  >
                    <Input
                      prefix={<MailOutlined style={{ color: '#bfbfbf' }} />}
                      type="email"
                      value={email}
                      disabled={isCodeSent}
                      onChange={(e) => setEmail(e.target.value)}
                      style={{ borderRadius: '6px' }}
                      placeholder="Enter your email"
                    />
                  </Form.Item>

                  {isCodeSent && (
                    <div style={{
                      transition: "opacity 300ms ease",
                      opacity: 1,
                      animation: "fadeIn 0.5s"
                    }}>
                      <Form.Item
                        label={<span style={{ color: '#1a3b63' }}>Verification Code (OTP)</span>}
                      >
                        <Input
                          prefix={<SafetyOutlined style={{ color: '#bfbfbf' }} />}
                          placeholder="Enter 6-digit code"
                          value={otp}
                          onChange={(e) => setOtp(e.target.value)}
                          style={{ borderRadius: '6px' }}
                          maxLength={6}
                        />
                      </Form.Item>
                    </div>
                  )}

                  {isCodeSent && (
                    <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                      {timerActive ? (
                        <Typography.Text type="secondary">
                          Resend code in {timer}s
                        </Typography.Text>
                      ) : (
                        <Typography.Link
                          onClick={handleGetCode}
                          style={{ color: '#1a3b63' }}
                        >
                          Resend Code
                        </Typography.Link>
                      )}
                    </div>
                  )}

                  <Form.Item>
                    {isCodeSent ? (
                      <Button
                        type="primary"
                        onClick={handleVerifyCode}
                        block
                        style={buttonStyle}
                        loading={loading}
                      >
                        Verify Code
                      </Button>
                    ) : (
                      <Button
                        type="primary"
                        onClick={handleGetCode}
                        block
                        style={buttonStyle}
                        loading={loading}
                      >
                        Send OTP
                      </Button>
                    )}
                  </Form.Item>
                </Form>
              </Card>
            )}

            {/* Reset Password Card */}
            {currentCard === "resetPassword" && (
              <Card
                title={<div style={{ textAlign: 'center', fontSize: '20px', color: '#1a3b63' }}>Reset Password</div>}
                style={cardStyle}
                styles={{ body: { padding: '20px 24px' } }}
              >
                <div style={{ position: 'absolute', top: '16px', left: '16px' }}>
                  <a
                    onClick={handleBackToLogin}
                    style={{ color: '#1a3b63', fontSize: '14px', display: 'flex', alignItems: 'center' }}
                  >
                    <ArrowLeftOutlined style={{ marginRight: '5px' }} /> Login
                  </a>
                </div>
                <Form layout="vertical" onFinish={handleUpdatePassword} size="large">
                  <Form.Item
                    label={<span style={{ color: '#1a3b63' }}>New Password</span>}
                    required
                  >
                    <Input.Password
                      prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
                      placeholder="Enter new password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      style={{ borderRadius: '6px' }}
                    />
                  </Form.Item>
                  <Form.Item
                    label={<span style={{ color: '#1a3b63' }}>Confirm Password</span>}
                    required
                    validateStatus={
                      confirmPassword && confirmPassword !== password ? "error" : ""
                    }
                    help={
                      confirmPassword && confirmPassword !== password
                        ? "Passwords do not match"
                        : ""
                    }
                  >
                    <Input.Password
                      prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      style={{ borderRadius: '6px' }}
                    />
                  </Form.Item>
                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      block
                      style={buttonStyle}
                      loading={loading}
                    >
                      Update Password
                    </Button>
                  </Form.Item>
                </Form>
              </Card>
            )}
          </div>

          <Card
            style={{
              ...cardStyle,
              padding: '12px',
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              textAlign: 'center',
              marginTop: '20px'
            }}
            bordered={false}
          >
            <Typography.Text style={{ color: '#1a3b63' }}>
              Inferno ERP &copy; Bitsmith Technologies Pvt. Ltd.
            </Typography.Text>
            {/* <div style={{ marginTop: '10px' }}>
              <a
                onClick={() => navigate('/about')}
                style={{ 
                  color: '#1a3b63', 
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                About This Product
              </a>
            </div> */}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Login;
